import { faChartGantt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useContext, useState } from 'react';

import AppContext from '@/contexts/appContext';
import { usePersonClassifications } from '@/hooks/usePerson';
import PersonContext from '@/pages/Person/PersonContext';
import SectionHeading from '@/pages/Profile/components/SectionHeading';
import { Classification } from '@/types/classification';
import { pillColorClasses, pillVariants } from '@/ui';
import Tooltip from '@/ui/components/Tooltip';
import { daysBetween, formatShortDate } from '@/utils/dates';

import ClassificationsChart from './ClassificationsChart';
import { ChartTheme, SortBy } from './types';

const RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days', days: 7 },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: '60', label: 'Last 60 days', days: 60 },
  { value: '90', label: 'Last 90 days', days: 90 },
  { value: 'all', label: 'All time', days: null }
];

// Matches the size of the classification badges it sits beside.
const chartPill = pillVariants({ size: 'sm' });

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: 'key', label: 'Sort alphabetically' },
  { value: 'active_from', label: 'Sort by active on' }
];

// Floor rather than round up so a classification added earlier today reads as
// "today" instead of claiming a full day it hasn't reached.
const durationLabel = (active_from: string) => {
  const days = Number(daysBetween(new Date(active_from), new Date(), true));

  if (days === 0) return 'Today';
  return `${days} ${days === 1 ? 'day' : 'days'}`;
};

// The API only ever returns classifications with a null active_until here, so
// there is no end date to show — just when it started and how long it's run.
const activeSince = ({ active_from }: Classification) =>
  `Active: ${formatShortDate(active_from)} (${durationLabel(active_from)})`;

const ClassificationsSection = () => {
  const {
    person: { id: personId, active_classifications }
  } = useContext(PersonContext);
  const { resolvedTheme, darkStyle } = useContext(AppContext);

  // Vega can't read the page theme off CSS, so resolve it here and hand the
  // chart the palette name.
  const chartTheme: ChartTheme = resolvedTheme === 'dark' ? darkStyle : 'light';

  const [showChart, setShowChart] = useState(false);
  const [rangeValue, setRangeValue] = useState('all');
  const [sortBy, setSortBy] = useState<SortBy>('key');
  const [activeOnly, setActiveOnly] = useState(false);
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);

  // Clicking a badge is only meaningful once the chart it highlights is on
  // screen, so reveal the chart rather than leaving the click looking inert.
  const handleBadgeClick = (key: string) => {
    setHighlightedKey((prev) => (prev === key ? null : key));
    setShowChart(true);
  };

  const rangeDays = RANGE_OPTIONS.find((option) => option.value === rangeValue)?.days ?? null;

  const { data: classificationsResponse, isPending } = usePersonClassifications({
    personId,
    enabled: showChart
  });

  return (
    <div>
      <SectionHeading text="Classifications" />
      <div className="flex flex-row items-center flex-wrap gap-1">
        {active_classifications.map((classification) => (
          <button
            key={classification.key}
            type="button"
            onClick={() => handleBadgeClick(classification.key)}
            aria-pressed={highlightedKey === classification.key}
            className={`px-2 py-1 rounded-sm text-sm ${
              highlightedKey === classification.key
                ? 'bg-gray-400 text-white'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}>
            <Tooltip
              tooltipContent={
                <div className="flex flex-col gap-0.5">
                  <span>{classification.description || 'No description available'}</span>
                  <span className="text-uniform-gray-200">{activeSince(classification)}</span>
                </div>
              }>
              {classification.key}
            </Tooltip>
          </button>
        ))}
        {/* Styled from the Pill recipe rather than wrapping the Pill component,
            which renders a span and would nest badly inside a button.
            Blue and outlined so it reads as an action sitting among the badges
            rather than as one more classification. */}
        <button
          type="button"
          onClick={() => setShowChart((prev) => !prev)}
          className={`${chartPill.root()} ml-1 ${
            showChart
              ? pillColorClasses.secondary.filled
              : `${pillColorClasses.secondary.outline} hover:bg-blue-50 dark:hover:bg-blue-900`
          } transition-colors focus-visible:ring-1 focus-visible:ring-current [outline:none]`}
          aria-label={showChart ? 'Hide classifications chart' : 'Show classifications chart'}
          aria-pressed={showChart}>
          <FontAwesomeIcon icon={faChartGantt} className={chartPill.icon()} />
          {showChart ? 'Hide chart' : 'Chart'}
        </button>
      </div>
      {showChart && (
        <>
          <div className="flex flex-wrap items-center justify-end gap-2 mt-2">
            <label className="flex items-center gap-1 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(event) => setActiveOnly(event.target.checked)}
                className="rounded border-gray-300"
              />
              Active only
            </label>
            <select
              aria-label="Classifications chart sort order"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value as SortBy)}
              className="rounded border border-gray-300 px-2 py-1 text-sm">
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Classifications chart date range"
              value={rangeValue}
              onChange={(event) => setRangeValue(event.target.value)}
              className="rounded border border-gray-300 px-2 py-1 text-sm">
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {isPending ? (
            <div className="animate-pulse mt-2" aria-hidden="true">
              <div className="h-40 bg-gray-100 rounded flex flex-col justify-around p-3">
                <div className="h-5 bg-gray-200 rounded w-3/4" />
                <div className="h-5 bg-gray-200 rounded w-1/2 ml-8" />
                <div className="h-5 bg-gray-200 rounded w-2/3 ml-4" />
                <div className="h-5 bg-gray-200 rounded w-5/6" />
              </div>
            </div>
          ) : (
            <ClassificationsChart
              activeOnly={activeOnly}
              classifications={classificationsResponse?.data ?? []}
              highlightedKey={highlightedKey}
              rangeDays={rangeDays}
              sortBy={sortBy}
              theme={chartTheme}
            />
          )}
        </>
      )}
    </div>
  );
};

export default memo(ClassificationsSection);
